import { useCallback, useMemo } from 'react';

import { useApplicationContext } from 'shared/utils/applicationContext';
import {
  caHostArticle,
  caHostButton,
  caHostHowItsWork,
  caHostPageCategory,
  caHostQuestion,
  caHostSocial,
  type IHostTrackingUser,
} from 'shared/utils/tracking';

export type TUseHostTrackingResult = {
  trackPageCategory: () => void;
  trackArticleClick: (articleName: string, articleLink: string, categoryName?: string) => void;
  trackQuestion: (question: string) => void;
  trackPlaceAdClick: (bannerName: string) => void;
  trackHowItsWork: () => void;
  trackSocialClick: (social: string) => void;
};

export const useHostTracking = (): TUseHostTrackingResult => {
  const { custom } = useApplicationContext();
  const user = useMemo<IHostTrackingUser>(
    () => ({
      userId: custom.userId ?? 0,
      isAuthorized: custom.isAuthorized ?? false,
    }),
    [custom.isAuthorized, custom.userId],
  );

  const trackPageCategory = useCallback((): void => {
    caHostPageCategory(user);
  }, [user]);

  const trackArticleClick = useCallback(
    (articleName: string, articleLink: string, categoryName?: string): void => {
      caHostArticle(user, {
        article: articleName,
        articleLink,
        categoryName,
      });
    },
    [user],
  );

  const trackQuestion = useCallback(
    (question: string): void => {
      caHostQuestion(user, { question });
    },
    [user],
  );

  const trackPlaceAdClick = useCallback(
    (bannerName: string): void => {
      caHostButton(user, { bannerName });
    },
    [user],
  );

  const trackHowItsWork = useCallback((): void => {
    caHostHowItsWork(user);
  }, [user]);

  const trackSocialClick = useCallback(
    (social: string): void => {
      caHostSocial(user, { social: social === 'vkVideo' ? 'vkvideo' : social });
    },
    [user],
  );

  return {
    trackPageCategory,
    trackArticleClick,
    trackQuestion,
    trackPlaceAdClick,
    trackHowItsWork,
    trackSocialClick,
  };
};
